import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useEffect } from 'react'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useLazyPrintPayConfirmQuery } from '@/store/api/procurementApi'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: btnXNTTver2 "Print - XNTT" → rpProcurementPayConfirm_* (Pay Confirm).
// BE chọn variant theo row (pay_data_confirm_print). Self-fetching dialog: lazy-fires
// the print query on open, renders returned `html` into an iframe srcDoc + Print button.
// Mirrors ProcurementLiquidPrintDialog.

interface Props {
  open: boolean
  /** Account chỉ View vẫn xem Preview, nhưng ẩn nút Print (in thật). */
  canEdit?: boolean
  paydataId: number | null
  xnttNo?: string | null
  /** payment_type của record — phần cuối tên file. */
  paymentType?: string | null
  /** contract_no của record — prefix tên file "<contract no>_<xntt no>_<payment type>.pdf". */
  contractNo?: string | null
  onClose: () => void
}

export default function PayDataConfirmPrintDialog({ open, canEdit = true, paydataId, xnttNo, paymentType, contractNo, onClose }: Props) {
  const snack = useSnackbar()
  const [trigger, { data, isFetching, isError, error, originalArgs }] = useLazyPrintPayConfirmQuery()

  useEffect(() => {
    if (open && paydataId != null) {
      void trigger(paydataId)
    }
  }, [open, paydataId, trigger])

  // Chỉ nhận kết quả khi đã settle (không fetching) VÀ khớp record đang mở —
  // tránh flash report cũ khi đổi dòng (useLazyQuery giữ data lần trước).
  const settled = !isFetching && originalArgs === paydataId
  const rawHtml = settled ? (data?.data?.html ?? '') : ''
  const previewHtml = rawHtml
    ? buildPrintPreviewHtml(rawHtml, {
        styleId: 'payconfirm-print-preview-overrides',
        layout: 'flow',
        // Số trong ô bảng gridline đang dính sát lề → thêm padding ngang nhỏ cho
        // dễ đọc (box-sizing:border-box nên KHÔNG lệch gridline). Chỉ Pay Confirm.
        extraCss: '.ctl.cell { padding-left: 0.9mm !important; padding-right: 0.9mm !important; }',
      })
    : ''
  const iframeTitle = 'Pay Confirm Print Preview'
  const label = xnttNo || (paydataId != null ? String(paydataId) : '')
  // Access btnXNTTver2 đặt title report theo layout variant (Advanced / P fix /
  // Outright / Provisional / Fixation …). BE trả về `title` đó → hiện lên caption
  // Print Preview thay cho tên chung, kèm XNTT để nhận diện record.
  // Bỏ tiền tố "Procurement - " (và biến thể "Procurement-" của Provisional) cho
  // caption gọn — title report BE giữ nguyên 1:1 Access cho document in.
  const layoutTitle = settled ? (data?.data?.title ?? '').replace(/^Procurement\s*-\s*/i, '') : ''
  const headerMain = layoutTitle ? `${layoutTitle} — XNTT ${label}` : `XNTT (${label})`

  useEffect(() => {
    if (open && settled && isError) {
      const detail =
        (error as { data?: { detail?: string } } | undefined)?.data?.detail ?? 'Cannot load Pay Confirm.'
      snack.error(detail)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settled, isError, error])

  // Loading tới khi API print trả về: dialog mở NGAY (feedback tức thì khi bấm
  // Pr-XNTT), spinner trong DialogContent, chỉ render iframe khi có html.
  const errored = settled && isError
  const loading = !rawHtml && !errored

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
        <Box component="span" title={`Print Preview - ${headerMain}`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
          {headerMain}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} size="small">Cancel</Button>
          {canEdit && (
          <Button
            variant="contained"
            size="small"
            disabled={!rawHtml}
            onClick={() => {
              // Tên file = "<contract no>_<xntt no>_<payment type>.pdf".
              const namePart = [(contractNo ?? '').trim(), label, (paymentType ?? '').trim()].filter(Boolean).join('_')
              const printTitle = `${sanitizePdfFileNamePart(namePart || 'pay_confirm')}.pdf`
              void printAndUploadReport({
                iframeTitle,
                reportName: 'rpProcurementPayConfirm',
                printTitle,
                html: previewHtml,
                onUploadError: snack.error,
              })
            }}
          >
            Print
          </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8, height: '100%' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : rawHtml ? (
          <Box component="iframe" title={iframeTitle} srcDoc={previewHtml} sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Cannot load Pay Confirm.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
